import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ensureCsrf, onUnauthorized, resetCsrf, ApiError } from '../api/client';
import { auth } from '../api/endpoints';
import type { User } from '../api/types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** Первичная проверка сессии завершена */
  ready: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me();
      setUser(me ?? null);
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthorized) {
        setUser(null);
      } else {
        // Сервер недоступен / прочая ошибка — не сбрасываем сессию принудительно.
        setUser((prev) => prev);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureCsrf();
      try {
        const me = await auth.me();
        if (!cancelled) setUser(me ?? null);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => onUnauthorized(() => setUser(null)), []);

  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    try {
      await auth.login(username, password);
      // После входа токен мог быть ротирован — обновим и заберём профиль.
      await ensureCsrf(true);
      const me = await auth.me();
      setUser(me ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await auth.logout();
    } catch {
      // Даже если logout на backend недоступен — локально выходим.
    } finally {
      resetCsrf();
      setUser(null);
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, ready, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth вне AuthProvider');
  return ctx;
}
